import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { MessageSquare, TrendingUp, AlertCircle, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/auth-context';
import { API_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';

interface UsageData {
  conversations_used: number;
  sessions_used: number;
  total_quota: number;
  remaining_quota: number;
  quota_exhausted: boolean;
  total_conversations: number;
}

interface UsageWidgetProps {
  siteId: string;
  compact?: boolean;
}

export const UsageWidget: React.FC<UsageWidgetProps> = ({ siteId, compact = false }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token && siteId) {
      fetchUsage();
    }
  }, [token, siteId]);

  const fetchUsage = async () => {
    try {
      setLoading(true);
      // Use the correct endpoint
      const response = await fetch(`${API_URL}/referrals/quota/${siteId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch usage');
      
      const data = await response.json();
      setUsage(data.quota);
    } catch (error) {
      console.error('Error fetching usage:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !usage) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const usagePercentage = Math.min(100, ((usage.sessions_used || usage.conversations_used) / usage.total_quota) * 100);
  const isNearLimit = usagePercentage >= 80;
  const isAtLimit = usage.quota_exhausted;

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm">
          {usage.sessions_used || usage.conversations_used}/{usage.total_quota} sessions today
        </span>
        {isAtLimit && <Badge variant="destructive" className="text-xs">Limit Reached</Badge>}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Daily Usage</CardTitle>
          {isNearLimit && !isAtLimit && (
            <Badge variant="warning" className="text-xs">
              <AlertCircle className="h-3 w-3 mr-1" />
              Near Limit
            </Badge>
          )}
          {isAtLimit && (
            <Badge variant="destructive" className="text-xs">
              <AlertCircle className="h-3 w-3 mr-1" />
              Limit Reached
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Sessions Today</span>
            <span className="font-medium">
              {usage.sessions_used || usage.conversations_used} / {usage.total_quota}
            </span>
          </div>
          <Progress 
            value={usagePercentage} 
            className={`h-2 ${
              isAtLimit ? 'bg-red-100' : 
              isNearLimit ? 'bg-orange-100' : 
              'bg-gray-100'
            }`}
          />
          <p className="text-xs text-muted-foreground">
            {usage.remaining_quota} sessions remaining today
            {usage.total_conversations && ` (${usage.total_conversations} total conversations)`}
          </p>
        </div>

        {(isNearLimit || isAtLimit) && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">
              {isAtLimit 
                ? 'This site has reached its daily session limit. Upgrade your plan for more sessions.'
                : 'This site is approaching its daily session limit. Consider upgrading your plan.'}
            </p>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => navigate('/account/licensing')}
              >
                <TrendingUp className="h-3 w-3 mr-1" />
                Upgrade Plan
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => navigate('/account/licensing')}
              >
                Invite Friends
                <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};